import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API } from '@/utils/API';

interface CreateSubmissionParams {
  submissionFormId: string;
  /**
   * OV-1180: extra templates whose questions are merged into this one submission.
   * Only ever populated for a multi-sub-category Modification.
   */
  additionalFormIds?: string[];
  title?: string;
  ownerId?: string;
  siteName?: string;
  isSiteCreation?: boolean;
}

interface UseCreateSubmissionResult {
  createSubmission: (params: CreateSubmissionParams) => Promise<string | null>;
  isLoading: boolean;
  error: string | null;
  submissionId: string | null;
}

const createSubmissionAPI = async (
  params: CreateSubmissionParams
): Promise<{ id: string }> => {
  if (!params.submissionFormId) {
    throw new Error('Form ID is required');
  }

  const response = await API.authenticated.fetch(`/submissions`, {
    method: 'POST',
    body: JSON.stringify({
      submission_template_id: params.submissionFormId,
      additional_submission_template_ids: params.additionalFormIds ?? [],
      title: params.title,
      owner_id: params.ownerId,
      site_name: params.siteName,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    const errorMessage = Array.isArray(data.error)
      ? data.error.join(', ')
      : data.error || 'Failed to create submission';
    throw new Error(errorMessage);
  }

  return data;
};

export const useCreateSubmission = (): UseCreateSubmissionResult => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const createSubmission = useCallback(
    async (params: CreateSubmissionParams): Promise<string | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await createSubmissionAPI(params);

        if (!data) {
          throw new Error('Invalid response: missing form ID');
        }

        setSubmissionId(data.id);

        // Invalidate submissions cache to refresh tables after creation
        queryClient.invalidateQueries({ queryKey: ['submissions'] });

        // A new site-creation submission under a study owner can change that
        // study's accepts_more_sites/can_add_submissions (e.g. a Single Site
        // Protocol study just filled its one site slot) and its site list —
        // both are cached with a non-zero staleTime, so without this,
        // navigating back to the study page shows stale data (New Site /
        // Invite or Attach Site still appear as available) until the cache
        // naturally expires. Scoped to isSiteCreation since only site-creation
        // submissions can affect a study's site count — other submission
        // types (modifications, closures, etc.) under the same ownerId leave
        // it unchanged. Mirrors Studies/Show.tsx's handleConnectSiteSuccess
        // for the sibling attach/invite flow.
        if (params.isSiteCreation && params.ownerId) {
          queryClient.invalidateQueries({
            queryKey: ['study', params.ownerId],
          });
          queryClient.invalidateQueries({ queryKey: ['sites'] });
        }

        return data.id;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to create submission'
        );
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient]
  );

  return {
    createSubmission,
    isLoading,
    error,
    submissionId,
  };
};
